// ComboBoxProject.tsx
import React, { useState, useEffect, useCallback } from 'react';
import Select, { type SingleValue } from 'react-select';
import CorgeeStorageHelper from '@components/Globals/CorgeeStorageHelper';
// import ProjectService from '../services/Project/ProjectService';
import { dashboardService } from '@services/dashboardService';
// ==================== TYPES ====================

interface ProjectRequest {
  ConfigValueId?: number;
  Filters?: any[];
  IsFromProject?: boolean;
  ProjectName?: string;
  GroupByLayerId?: number;
  GroupByDetailId?: number;
  GroupByValueField?: string;
  [key: string]: any;
}

interface Project {
  ProjectID: number;
  Name: string;
  [key: string]: any;
}

interface ProjectsResponse {
  Projects: Project[];
}

interface ProjectOption {
  value: number | null;
  label: string;
}

interface ComboBoxProjectProps {
  /**
   * Request object containing API config and request parameters
   */
  requestVM: ProjectRequest;
  /**
   * Initial project ID to be selected
   */
  initialValue?: number | null;
  /**
   * Callback function when a project is selected
   */
  onDataSelected?: (item: ProjectOption | null) => void;
  /**
   * Callback function when input loses focus
   */
  onBlur?: () => void;
  /**
   * Flag to open dropdown on focus
   */
  openOnFocus?: boolean;
  /**
   * Flag to autofocus the input on mount
   */
  autoFocus?: boolean;
  /**
   * Custom name for the form field
   */
  name?: string;
  /**
   * Custom placeholder text
   */
  placeholder?: string;
  /**
   * Custom styles for the Select component
   */
  style?: React.CSSProperties;
}

// ==================== COMPONENT ====================

/**
 * ComboBoxProject - A select component for choosing projects
 * Fetches projects from API and displays them in a searchable dropdown
 */
const ComboBoxProject: React.FC<ComboBoxProjectProps> = ({
  requestVM,
  initialValue = null,
  onDataSelected,
  onBlur,
  openOnFocus = false,
  autoFocus: _autoFocus = false,
  name = 'form-field-name',
  placeholder,
  style,
}) =>
{
  const [dataSelected, setDataSelected] = useState<ProjectOption>({
    value: initialValue,
    label: '',
  });
  const [data, setData] = useState<Project[]>([]);
  const [loading, setLoading] = useState(false);

  // Get project name configuration from session storage
  const nameProjectConfig = CorgeeStorageHelper.getProjectNameConfig();

  // Load projects on mount
  useEffect(() =>
  {
    const loadProjects = async () =>
    {
      try
      {
        setLoading(true);
        const response: ProjectsResponse = await dashboardService.getSideMenuProjects(9);

        if (response && response.Projects)
        {
          setData(response.Projects);

          // If there's an initial value, find and set the selected project
          if (initialValue)
          {
            const initialProject = response.Projects.find(
              project => project.ProjectID === initialValue,
            );
            if (initialProject)
            {
              setDataSelected({
                value: initialProject.ProjectID,
                label: initialProject.Name,
              });
            }
          }
        }
      }
      catch (error)
      {
        console.error('Error loading projects:', error);
        setData([]);
      }
      finally
      {
        setLoading(false);
      }
    };

    loadProjects();
  }, [requestVM, initialValue]);

  // Handle selection change
  const handleSelectedChange = useCallback(
    (selectedOption: SingleValue<ProjectOption>) =>
    {
      if (selectedOption)
      {
        setDataSelected(selectedOption);
        if (onDataSelected)
        {
          onDataSelected(selectedOption);
        }
      }
      else
      {
        setDataSelected({ value: null, label: '' });
        if (onDataSelected)
        {
          onDataSelected(null);
        }
      }
    },
    [onDataSelected],
  );

  // Handle blur event
  const handleBlur = useCallback(() =>
  {
    if (onBlur)
    {
      onBlur();
    }
  }, [onBlur]);

  // Transform projects data to options format
  const projectOptions: ProjectOption[] = data.map(project => ({
    value: project.ProjectID,
    label: project.Name,
  }));

  // Generate placeholder text
  let projectNameConfigStr = 'Projects';
  if (nameProjectConfig !== undefined && nameProjectConfig !== null)
  {
    projectNameConfigStr = nameProjectConfig.toLowerCase();
  }

  const placeholderText = placeholder || `Select ${projectNameConfigStr}...`;

  return (
    <div style={style}>
      <Select<ProjectOption>
        name={name}
        value={dataSelected.value ? dataSelected : null}
        options={projectOptions}
        onChange={handleSelectedChange}
        onBlur={handleBlur}
        placeholder={placeholderText}
        isSearchable={true}
        openMenuOnFocus={openOnFocus}
        // autoFocus={autoFocus}
        isLoading={loading}
        isClearable={true}
        styles={{
          container: base => ({
            ...base,
            width: '300px',
            margin: '20px',
            marginBottom: '30px',
            marginLeft: '0',
            marginTop: '0',
          }),
        }}
      />
    </div>
  );
};

export default ComboBoxProject;
